import type { ATSJob, StageMapping } from '@kiku/integrations/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DepartmentBreadcrumb } from '~/components/features/departments/department-breadcrumb';
import type { WizardQuestion } from '~/components/features/interviews/interviews.types';
import { useRegenerateScoring } from '~/components/features/interviews/use-regenerate-scoring';
import { WizardStepHeader } from '~/components/features/interviews/wizard-step-header';
import { AtsSelectorStep } from '~/components/features/interviews/wizard-steps/ats-selector-step';
import { AtsStageSelectorStep } from '~/components/features/interviews/wizard-steps/ats-stage-selector-step';
import { ConfirmStep } from '~/components/features/interviews/wizard-steps/confirm-step';
import { QuestionsStep } from '~/components/features/interviews/wizard-steps/questions-step';
import { SharedSettingsStep } from '~/components/features/interviews/wizard-steps/shared-settings-step';
import { PageContainer } from '~/components/layouts/page-container';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';

export const Route = createFileRoute(
  '/_authenticated/org/$orgSlug/department/$deptSlug/jobs/import/'
)({
  component: ImportWizard
});

type SharedSettings = {
  employer: string;
  modality: Array<'text' | 'voice'>;
  languages: string[];
};

type WizardState = {
  step: 0 | 1 | 2 | 3 | 4;
  selectedAtsJobs: ATSJob[];
  atsProvider: 'teamtailor' | 'recman' | null;
  stageSelections: Record<string, StageMapping> | null;
  jobIds: string[];
  interviewIds: string[];
  sharedSettings: SharedSettings | null;
  questions: WizardQuestion[];
};

type WizardContextType = WizardState & {
  setState: React.Dispatch<React.SetStateAction<WizardState>>;
  orgId: string;
  deptId: string;
  orgSlug: string;
  deptSlug: string;
};

const WizardContext = createContext<WizardContextType | null>(null);

function useWizard(): WizardContextType {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used inside WizardContext');
  return ctx;
}

function Step0(): React.ReactNode {
  const { setState, selectedAtsJobs, orgId } = useWizard();

  const { data, isLoading } = useQuery({
    queryKey: ['integrations', 'jobs', orgId] as const,
    queryFn: async () => {
      const res = await api.api.org[':orgId'].integrations.jobs.$get({
        param: { orgId },
        query: { excludeActive: 'true' }
      });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
      return res.json();
    },
    enabled: !!orgId
  });

  const { data: configData } = useQuery({
    queryKey: ['integrations', 'config', orgId] as const,
    queryFn: async () => {
      const res = await api.api.org[':orgId'].integrations.$get({ param: { orgId } });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
      return res.json();
    },
    enabled: !!orgId
  });

  const atsJobs = (data as { jobs?: ATSJob[] } | undefined)?.jobs;
  const provider =
    (configData as { config?: { provider: 'teamtailor' | 'recman' } | null } | undefined)?.config
      ?.provider ?? null;

  useEffect(() => {
    if (provider !== null) {
      setState((prev) => ({ ...prev, atsProvider: provider }));
    }
  }, [provider]);

  return (
    <AtsSelectorStep
      atsJobs={atsJobs}
      isLoading={isLoading}
      selectedIds={selectedAtsJobs.map((j) => j.id)}
      onSelectionChange={(ids) => {
        const selected = (atsJobs ?? []).filter((j) => ids.includes(j.id));
        setState((prev) => ({ ...prev, selectedAtsJobs: selected }));
      }}
      // Teamtailor requires per-job stage mapping (step 1). Selecting multiple jobs would
      // force users to configure stage mappings for each one individually — poor UX.
      // Limit to one job at a time so the stage selection step is manageable.
      singleSelect={provider === 'teamtailor'}
      onContinue={() => {
        const nextStep = provider === 'teamtailor' ? 1 : 2;
        setState((prev) => ({
          ...prev,
          step: nextStep as WizardState['step'],
          atsProvider: provider
        }));
      }}
    />
  );
}

function Step1(): React.ReactNode {
  const { setState, selectedAtsJobs, orgId } = useWizard();

  return (
    <AtsStageSelectorStep
      selectedAtsJobs={selectedAtsJobs}
      orgId={orgId}
      onComplete={(stageSelections) => {
        setState((prev) => ({ ...prev, step: 2, stageSelections }));
      }}
      onBack={() => {
        setState((prev) => ({ ...prev, step: 0 }));
      }}
    />
  );
}

function Step2(): React.ReactNode {
  const { setState, atsProvider } = useWizard();

  return (
    <SharedSettingsStep
      onSubmit={(settings) => {
        setState((prev) => ({
          ...prev,
          step: 3,
          sharedSettings: settings
        }));
      }}
      onBack={() => {
        const prevStep = atsProvider === 'teamtailor' ? 1 : 0;
        setState((prev) => ({ ...prev, step: prevStep as WizardState['step'] }));
      }}
      isSubmitting={false}
    />
  );
}

function Step3(): React.ReactNode {
  const {
    setState,
    selectedAtsJobs,
    sharedSettings,
    stageSelections,
    questions,
    jobIds,
    interviewIds,
    orgId,
    deptId
  } = useWizard();
  const [isGenerating, setIsGenerating] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const hasFetched = useRef(false);

  // Import jobs and generate questions on mount — skip if already done (e.g. navigating back then forward)
  useEffect(() => {
    if (questions.length > 0) {
      setIsGenerating(false);
      return;
    }
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (!sharedSettings) return;
    void (async () => {
      try {
        // Import all selected ATS jobs
        const importRes = await api.api.org[':orgId'].department[':deptId'].jobs.import.$post({
          param: { orgId, deptId },
          json: {
            atsJobIds: selectedAtsJobs.map((j) => j.id),
            employer: sharedSettings.employer,
            modality: sharedSettings.modality,
            languages: sharedSettings.languages,
            ...(stageSelections && { stageSelections })
          }
        });
        if (!importRes.ok) throw new Error(i18n.t('errors.unexpected'));
        const { jobs: importedJobs } = await importRes.json();
        const ids = importedJobs.map((j: { id: string }) => j.id);
        const iIds = importedJobs
          .map((j: { interviewId: string | null }) => j.interviewId)
          .filter((id: string | null): id is string => id !== null);
        setState((prev) => ({ ...prev, jobIds: ids, interviewIds: iIds }));

        // Generate questions using the first job as context
        if (ids[0]) {
          const genRes = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'][
            'interview-setup'
          ].questions.generate.$post({ param: { orgId, deptId, jobId: ids[0] } });
          if (!genRes.ok) throw new Error(i18n.t('errors.unexpected'));
          const { questions: generated } = await genRes.json();
          setState((prev) => ({
            ...prev,
            questions: generated.map((q) => ({
              id: q.id,
              category: q.category,
              text: q.text,
              position: q.position,
              isOptional: q.isOptional,
              competencyName: q.competencyName ?? undefined,
              scoringAnchors: q.scoringAnchors ?? undefined,
              aiGenerated: q.aiGenerated
            }))
          }));
        }
      } finally {
        setIsGenerating(false);
      }
    })();
  }, []);

  async function handleSave(): Promise<void> {
    if (jobIds.length === 0) return;
    setIsSaving(true);
    try {
      // Apply questions to all imported jobs
      await api.api.org[':orgId'].department[':deptId'].jobs[':jobId']['interview-setup'].questions[
        'apply-to-jobs'
      ].$post({
        param: { orgId, deptId, jobId: jobIds[0] ?? '' },
        json: {
          sourceQuestions: questions.map((q) => ({
            category: q.category,
            text: q.text,
            position: q.position,
            isOptional: q.isOptional,
            competencyName: q.competencyName,
            scoringAnchors: q.scoringAnchors
          })),
          interviewIds
        }
      });
      setState((prev) => ({ ...prev, step: 4 }));
    } finally {
      setIsSaving(false);
    }
  }

  const handleRegenerateScoring = useRegenerateScoring(orgId, deptId, jobIds[0] ?? null);

  return (
    <QuestionsStep
      questions={questions}
      isGenerating={isGenerating}
      onQuestionsChange={(qs) => {
        setState((prev) => ({ ...prev, questions: qs }));
      }}
      onBack={() => {
        setState((prev) => ({ ...prev, step: 2 }));
      }}
      onSave={() => void handleSave()}
      isSaving={isSaving}
      onRegenerateScoring={handleRegenerateScoring}
    />
  );
}

function Step4(): React.ReactNode {
  const { selectedAtsJobs, questions, sharedSettings, jobIds, orgId, deptId, orgSlug, deptSlug } =
    useWizard();
  const navigate = useNavigate();
  const { setState } = useWizard();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  async function handleGoLive(): Promise<void> {
    setIsSubmitting(true);
    try {
      await api.api.org[':orgId'].department[':deptId'].jobs['bulk-activate'].$post({
        param: { orgId, deptId },
        json: { jobIds }
      });
      await queryClient.invalidateQueries({ queryKey: ['jobs', orgId, deptId] });
      void navigate({
        to: '/org/$orgSlug/department/$deptSlug/jobs',
        params: { orgSlug, deptSlug }
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ConfirmStep
      jobs={selectedAtsJobs.map((j) => ({
        title: j.title,
        description: j.description,
        atsExternalJobId: j.id,
        location: j.location
      }))}
      questions={questions}
      modality={sharedSettings?.modality ?? ['text']}
      languages={sharedSettings?.languages ?? []}
      onBack={() => {
        setState((prev) => ({ ...prev, step: 3 }));
      }}
      onGoLive={() => void handleGoLive()}
      isSubmitting={isSubmitting}
    />
  );
}

function ImportWizard(): React.ReactNode {
  const { orgSlug, deptSlug } = Route.useParams();
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);
  const { t } = useTranslation();
  useDocumentTitle(t('wizard.import.title'));

  const [state, setState] = useState<WizardState>({
    step: 0,
    selectedAtsJobs: [],
    atsProvider: null,
    stageSelections: null,
    jobIds: [],
    interviewIds: [],
    sharedSettings: null,
    questions: []
  });

  const isTeamtailor = state.atsProvider === 'teamtailor';
  const importSteps = useMemo(
    (): { label: string; internalStep: WizardState['step'] }[] =>
      isTeamtailor
        ? [
            { label: t('wizard.import.step0Title'), internalStep: 0 },
            { label: t('wizard.import.step1Title'), internalStep: 1 },
            { label: t('wizard.import.step2Title'), internalStep: 2 },
            { label: t('wizard.import.step3Title'), internalStep: 3 },
            { label: t('wizard.import.step4Title'), internalStep: 4 }
          ]
        : [
            { label: t('wizard.import.step0Title'), internalStep: 0 },
            { label: t('wizard.import.step2Title'), internalStep: 2 },
            { label: t('wizard.import.step3Title'), internalStep: 3 },
            { label: t('wizard.import.step4Title'), internalStep: 4 }
          ],
    [isTeamtailor, t]
  );
  const displayStep = isTeamtailor ? state.step : state.step > 0 ? state.step - 1 : 0;

  return (
    <WizardContext.Provider
      value={{ ...state, setState, orgId, deptId: deptId ?? '', orgSlug, deptSlug }}
    >
      <PageContainer
        title={t('wizard.import.title')}
        breadcrumbs={[
          { content: <DepartmentBreadcrumb orgSlug={orgSlug} deptSlug={deptSlug} /> },
          { label: t('jobs.title'), href: `/org/${orgSlug}/department/${deptSlug}/jobs` },
          { label: t('wizard.import.title') }
        ]}
      >
        <WizardStepHeader
          steps={importSteps.map((s) => ({ label: s.label }))}
          currentStepIndex={displayStep}
          onStepClick={(displayIndex) => {
            const target = importSteps[displayIndex];
            if (target) {
              setState((prev) => ({ ...prev, step: target.internalStep }));
            }
          }}
        />
        <div className="flex flex-1 min-h-0 flex-col">
          {state.step === 0 && <Step0 />}
          {state.step === 1 && <Step1 />}
          {state.step === 2 && <Step2 />}
          {state.step === 3 && <Step3 />}
          {state.step === 4 && <Step4 />}
        </div>
      </PageContainer>
    </WizardContext.Provider>
  );
}
